<template>
  <a-drawer title="查询" :visible="visible" @close="handleClose" placement="left" :mask="false" :width="300">
    <div class="query-content">
      <a-form-model :model="form"  ref="form">
        <a-form-model-item label="查询类型" prop="queryType">
          <a-radio-group v-model="form.queryType" @change="handleTypeChange">
            <a-radio value="remoteMeter">分区计量仪表</a-radio>
            <a-radio value="boundaryValve">分界阀门</a-radio>
          </a-radio-group>
        </a-form-model-item>

        <a-form-model-item label="编号" prop="deviceCode">
          <a-input v-model="form.deviceCode" :placeholder="getPlaceholder()" allowClear />
        </a-form-model-item>

        <a-form-model-item label="地址" prop="address">
          <a-input v-model="form.address" placeholder="请输入地址" allowClear />
        </a-form-model-item>

        <!-- 操作按钮 -->
        <a-form-model-item>
          <div style="text-align: center;">
            <a-space>
              <a-button type="primary" :loading="loading" @click="handleQuery">查询</a-button>
              <a-button @click="handleReset">重置</a-button>
            </a-space>
          </div>
        </a-form-model-item>
      </a-form-model>

      <!-- 查询结果列表 -->
      <div v-if="searchResults.length > 0" class="result-list">
        <div class="result-header">
          <span>查询结果 ({{ searchResults.length }}条)</span>
          <a-button type="link" size="small" @click="clearResults">清空</a-button>
        </div>
        <div class="result-items">
          <div 
            v-for="(item, index) in searchResults" 
            :key="index"
            class="result-item"
            :class="{ active: selectedIndex === index }"
            @click="handleSelectResult(item, index)"
          >
            <div class="result-icon">
              <img :src="getResultIcon()" alt="icon" />
            </div>
            <div class="result-info">
              <div class="result-title">{{ getResultTitle(item) }}</div>
              <div class="result-desc">{{ getResultDesc(item) }}</div>
            </div>
            <a-icon type="environment" class="location-icon" />
          </div>
        </div>
      </div>

      <!-- 无结果提示 -->
      <div v-else-if="hasSearched && searchResults.length === 0" class="no-result">
        <a-empty description="未找到匹配的设备" :image="simpleImage" />
      </div>
    </div>
  </a-drawer>
</template>

<script>
import { Empty } from 'ant-design-vue';

export default {
  name: 'PartitionQueryDrawer',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    currentZoneId: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      form: {
        queryType: 'remoteMeter',
        deviceCode: '',
        address: ''
      },
     
      remoteMetersUrl: '/pipe-network/remote-meters/rule/select',
      boundaryValvesUrl: '/pipe-network/water_associated_points/list',
      loading: false,
      searchResults: [],
      selectedIndex: -1,
      hasSearched: false,
      simpleImage: Empty.PRESENTED_IMAGE_SIMPLE
    }
  },
  watch: {
    'form.queryType' () {
      this.clearResults();
    }
  },
  methods: {
    handleClose () {
      this.$emit('close');
    },

    handleTypeChange () {
      this.form.deviceCode = '';
      this.clearResults();
    },

    getPlaceholder () {
      return this.form.queryType === 'remoteMeter' 
        ? '请输入分区计量仪表编号' 
        : '请输入分界阀门编号';
    },

    async handleQuery () {
      this.$refs.form.validate(async (valid) => {
        if (valid) {
          this.loading = true;
          this.hasSearched = true;
          this.selectedIndex = -1;
          
          try {
            let deviceList = [];

            if (this.form.queryType === 'remoteMeter') {
              const params = { 
                appearanceNo: this.form.deviceCode,
                address: this.form.address || undefined
              };
              const res = await this.$http.get(this.remoteMetersUrl, { params });
              deviceList = res.data || [];
            } else {
              const params = { 
                classNo: this.form.deviceCode,
                address: this.form.address || undefined
              };
              const res = await this.$http.get(this.boundaryValvesUrl, { params });
              deviceList = res.data || [];
            }

            this.searchResults = deviceList;

            if (deviceList.length === 0) {
              this.$message.warning('未找到匹配的设备');
            } else if (deviceList.length === 1) {
              this.handleSelectResult(deviceList[0], 0);
            } else {
              this.$message.info(`找到 ${deviceList.length} 条结果，请选择查看`);
            }
          } catch (error) {
            console.error('查询失败:', error);
            this.$message.error('查询失败，请稍后重试');
            this.searchResults = [];
          } finally {
            this.loading = false;
          }
        }
      });
    },

    handleSelectResult (item, index) {
      this.selectedIndex = index;
      
      const isRemoteMeter = this.form.queryType === 'remoteMeter';
      const icon = isRemoteMeter 
        ? require('@/assets/myImg/1/流量计.png')
        : require('@/assets/myImg/1/icon-阀门深红@2x.png');
      
      this.$emit('location-device', {
        type: isRemoteMeter ? 'remoteMeter' : 'boundaryValve',
        device: item,
        icon
      });

      const deviceName = isRemoteMeter ? item.appearanceNo : item.classNo;
      const typeName = isRemoteMeter ? '分区计量仪表' : '分界阀门';
      // this.$message.success(`已定位到${typeName}: ${deviceName}`);
    },

    getResultIcon () {
      return this.form.queryType === 'remoteMeter'
        ? require('@/assets/myImg/1/流量计.png')
        : require('@/assets/myImg/1/icon-阀门深红@2x.png');
    },

    getResultTitle (item) {
      return this.form.queryType === 'remoteMeter'
        ? (item.appearanceNo || item.deviceNo || '未知编号')
        : (item.classNo || item.deviceNo || '未知编号');
    },

    getResultDesc (item) {
      if (this.form.queryType === 'remoteMeter') {
        return item.installAddress || item.address || '';
      }
      return item.address || item.installAddress || '';
    },

    clearResults () {
      this.searchResults = [];
      this.selectedIndex = -1;
      this.hasSearched = false;
    },

    handleReset () {
      this.$refs.form.resetFields();
      this.form.queryType = 'remoteMeter';
      this.form.deviceCode = '';
      this.form.address = '';
      this.clearResults();
    }
  }
}
</script>

<style lang="less" scoped>
.query-content {
  padding: 20px 10px;

  /deep/ .ant-form-item-label {
    label {
      font-weight: 500;
      color: #333;
    }
  }

  /deep/ .ant-radio-group {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .ant-radio-wrapper {
      margin-right: 0;
    }
  }

  /deep/ .ant-btn {
    min-width: 80px;
  }
}

.result-list {
  margin-top: 16px;
  border-top: 1px solid #e8e8e8;
  padding-top: 12px;

  .result-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    font-weight: 500;
    color: #333;
  }

  .result-items {
    max-height: 420px;
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: #d9d9d9;
      border-radius: 2px;
    }
  }

  .result-item {
    display: flex;
    align-items: center;
    padding: 10px 12px;
    margin-bottom: 8px;
    background: #fafafa;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
    border: 1px solid transparent;

    &:hover {
      background: #e6f7ff;
      border-color: #91d5ff;
    }

    &.active {
      background: #e6f7ff;
      border-color: #1890ff;
    }

    .result-icon {
      width: 32px;
      height: 32px;
      flex-shrink: 0;
      margin-right: 10px;

      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
    }

    .result-info {
      flex: 1;
      min-width: 0;

      .result-title {
        font-size: 14px;
        font-weight: 500;
        color: #333;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .result-desc {
        font-size: 12px;
        color: #999;
        margin-top: 2px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    .location-icon {
      flex-shrink: 0;
      font-size: 16px;
      color: #1890ff;
      margin-left: 8px;
    }
  }
}

.no-result {
  margin-top: 40px;
  text-align: center;
}
</style>

